import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft, Flame, Users, Calendar, CheckSquare, Square,
  Package, AlertTriangle, Loader2
} from 'lucide-react'
import {
  getLantanaPatches, getUsers, getBiocharSettings,
  planBatchCreation, bulkCreateBatches
} from '../../services/api'

export default function BatchForm({ ranchId }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [selectedPatchIds, setSelectedPatchIds] = useState([])
  const [operatorId, setOperatorId] = useState('')
  const [productionDate, setProductionDate] = useState(new Date().toISOString().split('T')[0])
  const [notes, setNotes] = useState('')
  const [error, setError] = useState('')
  const [plan, setPlan] = useState(null)
  const [step, setStep] = useState('select') // 'select' | 'review' | 'creating'

  // Fetch CLEARED vegetation patches (biomass ready for biochar)
  const { data: clearedPatches = [], isLoading: patchesLoading } = useQuery({
    queryKey: ['lantanaPatches', ranchId, 'cleared'],
    queryFn: () => getLantanaPatches(ranchId, { status: 'cleared' }),
  })

  // Fetch biochar settings (kiln capacity)
  const { data: biocharSettings } = useQuery({
    queryKey: ['biocharSettings', ranchId],
    queryFn: () => getBiocharSettings(ranchId),
  })

  // Fetch users who can be operators
  const { data: users } = useQuery({
    queryKey: ['users', ranchId],
    queryFn: () => getUsers(ranchId),
  })

  // biochar_operator is the role that runs batches; manager/owner included
  // as fallbacks when an operator isn't set up yet on a ranch.
  const operators = users?.filter(u =>
    ['biochar_operator', 'manager', 'owner'].includes(u.role_at_ranch) && u.is_active
  ) || []

  const kilnCapacity = biocharSettings?.standard_kiln_capacity_kg || 200

  // Available patches (not yet fully allocated)
  const availablePatches = clearedPatches
  const hasClearedPatches = availablePatches.length > 0

  // Calculate totals for selected patches
  const selectedPatches = useMemo(() =>
    availablePatches.filter(p => selectedPatchIds.includes(p.id)),
    [availablePatches, selectedPatchIds]
  )

  const totalBiomass = useMemo(() =>
    selectedPatches.reduce((sum, p) => sum + (parseFloat(p.estimated_biomass_kg) || 0), 0),
    [selectedPatches]
  )

  const estimatedBatches = Math.ceil(totalBiomass / kilnCapacity) || 0

  // Toggle patch selection
  const togglePatch = (patchId) => {
    setSelectedPatchIds(prev =>
      prev.includes(patchId)
        ? prev.filter(id => id !== patchId)
        : [...prev, patchId]
    )
    // Reset plan when selection changes
    setPlan(null)
    setStep('select')
  }

  const selectAll = () => {
    setSelectedPatchIds(availablePatches.map(p => p.id))
    setPlan(null)
    setStep('select')
  }

  const clearSelection = () => {
    setSelectedPatchIds([])
    setPlan(null)
    setStep('select')
  }

  // Plan batch creation
  const planMutation = useMutation({
    mutationFn: () => planBatchCreation(ranchId, selectedPatchIds),
    onSuccess: (data) => {
      setPlan(data)
      setStep('review')
      setError('')
    },
    onError: (err) => setError(err.response?.data?.detail || 'Failed to generate batch plan'),
  })

  // Bulk create batches
  const createMutation = useMutation({
    mutationFn: (payload) => bulkCreateBatches(payload),
    onSuccess: () => {
      queryClient.invalidateQueries(['biocharBatches'])
      queryClient.invalidateQueries(['lantanaPatches'])
      navigate('/biochar')
    },
    onError: (err) => setError(err.response?.data?.detail || 'Failed to create batches'),
  })

  const handleGeneratePlan = () => {
    setError('')
    if (selectedPatchIds.length === 0) {
      setError('Please select at least one patch')
      return
    }
    if (!operatorId) {
      setError('Please assign an operator')
      return
    }
    planMutation.mutate()
  }

  const handleConfirmCreate = () => {
    if (!plan) return
    setStep('creating')
    createMutation.mutate({
      ranch_id: ranchId,
      operator_id: operatorId,
      production_date: productionDate,
      plan: plan.batches,
      notes: notes || null,
    })
  }

  const selectedOperator = operators.find(o => o.id === operatorId)

  const vegTypeBadge = (type) => {
    const colors = {
      lantana: 'bg-red-100 text-red-700',
      pasture: 'bg-green-100 text-green-700',
      savannah_tree: 'bg-amber-100 text-amber-700',
      other: 'bg-gray-100 text-gray-700',
    }
    return colors[type] || colors.other
  }

  return (
    <div className="max-w-3xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create Biochar Batches</h1>
          <p className="text-gray-600">Select cleared patches, review batch plan, then confirm</p>
        </div>
      </div>

      {/* Workflow Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h3 className="font-medium text-blue-900 mb-2">Batch Planning Workflow</h3>
        <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
          <li className={step === 'select' ? 'font-bold' : ''}>
            Select cleared patches and assign operator
          </li>
          <li className={step === 'review' ? 'font-bold' : ''}>
            Review auto-generated batch plan (split by kiln capacity: {kilnCapacity} kg)
          </li>
          <li className={step === 'creating' ? 'font-bold' : ''}>
            Confirm and create batches
          </li>
        </ol>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      {/* Step 1: Production Date */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Calendar className="h-5 w-5 text-blue-600" />
          <h2 className="text-lg font-semibold text-gray-900">Production Date</h2>
        </div>

        <div>
          <input
            type="date"
            value={productionDate}
            onChange={(e) => setProductionDate(e.target.value)}
            className="w-full sm:w-64 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          />
          <p className="text-xs text-gray-500 mt-1">
            When should these batches be processed?
          </p>
        </div>
      </div>

      {/* Step 2: Select Patches */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Package className="h-5 w-5 text-green-600" />
            <h2 className="text-lg font-semibold text-gray-900">Select Biomass Source</h2>
          </div>
          {hasClearedPatches && (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={selectAll}
                className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
              >
                Select All
              </button>
              <button
                type="button"
                onClick={clearSelection}
                className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
              >
                Clear
              </button>
            </div>
          )}
        </div>

        {patchesLoading ? (
          <p className="text-gray-500">Loading cleared patches...</p>
        ) : hasClearedPatches ? (
          <>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {availablePatches.map(patch => {
                const isSelected = selectedPatchIds.includes(patch.id)
                return (
                  <button
                    key={patch.id}
                    type="button"
                    onClick={() => togglePatch(patch.id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${
                      isSelected
                        ? 'bg-green-50 border-green-300'
                        : 'bg-white border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {isSelected
                      ? <CheckSquare className="h-5 w-5 text-green-600 flex-shrink-0" />
                      : <Square className="h-5 w-5 text-gray-400 flex-shrink-0" />
                    }
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{patch.patch_code}</span>
                        <span className={`px-1.5 py-0.5 text-xs rounded capitalize ${vegTypeBadge(patch.vegetation_type)}`}>
                          {(patch.vegetation_type || 'lantana').replace('_', ' ')}
                        </span>
                      </div>
                      <div className="text-sm text-gray-500">
                        {patch.paddock_name || 'Unknown paddock'}
                        {patch.area_ha && <> &middot; {patch.area_ha} ha</>}
                        {patch.density && <> &middot; {patch.density}</>}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="font-medium text-gray-900">
                        {patch.estimated_biomass_kg ? `${patch.estimated_biomass_kg} kg` : '? kg'}
                      </div>
                      <div className="text-xs text-gray-500">biomass</div>
                    </div>
                  </button>
                )
              })}
            </div>

            {/* Selection Summary */}
            {selectedPatchIds.length > 0 && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-green-800">
                    <strong>{selectedPatchIds.length}</strong> patch{selectedPatchIds.length > 1 ? 'es' : ''} selected
                  </div>
                  <div className="text-sm text-green-800">
                    Total biomass: <strong>{totalBiomass.toFixed(0)} kg</strong>
                  </div>
                </div>
                <div className="text-xs text-green-700 mt-1">
                  Estimated batches: {estimatedBatches} (kiln capacity: {kilnCapacity} kg)
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="h-4 w-4 text-yellow-600" />
              <p className="text-sm font-medium text-yellow-800">No cleared patches available</p>
            </div>
            <p className="text-xs text-yellow-700">
              Mark vegetation patches as "cleared" in the Land Management section before creating biochar batches.
            </p>
          </div>
        )}
      </div>

      {/* Step 3: Assign Operator */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Users className="h-5 w-5 text-blue-600" />
          <h2 className="text-lg font-semibold text-gray-900">Assign Operator</h2>
        </div>

        <select
          value={operatorId}
          onChange={(e) => setOperatorId(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
        >
          <option value="">Select an operator</option>
          {operators.map(user => (
            <option key={user.id} value={user.id}>
              {user.full_name} ({user.role_at_ranch})
            </option>
          ))}
        </select>
        <p className="text-xs text-gray-500 mt-1">
          All batches will be assigned to this operator
        </p>
      </div>

      {/* Notes */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Notes</h2>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          rows={2}
          placeholder="Any special instructions for the operator..."
        />
      </div>

      {/* Generate Plan Button */}
      {step === 'select' && (
        <div className="flex justify-end mb-6">
          <button
            type="button"
            onClick={handleGeneratePlan}
            disabled={selectedPatchIds.length === 0 || !operatorId || planMutation.isPending}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {planMutation.isPending ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Generating Plan...
              </>
            ) : (
              <>
                <Package className="h-5 w-5" />
                Generate Batch Plan
              </>
            )}
          </button>
        </div>
      )}

      {/* Step 4: Review Plan */}
      {plan && step === 'review' && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Flame className="h-5 w-5 text-orange-600" />
            <h2 className="text-lg font-semibold text-gray-900">Batch Plan</h2>
          </div>

          <div className="grid grid-cols-3 gap-4 mb-4 p-3 bg-gray-50 rounded-lg">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">{plan.num_batches}</div>
              <div className="text-xs text-gray-500">Batches</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">{parseFloat(plan.total_biomass_kg).toFixed(0)}</div>
              <div className="text-xs text-gray-500">Total kg</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">{parseFloat(plan.kiln_capacity_kg).toFixed(0)}</div>
              <div className="text-xs text-gray-500">Kiln Capacity kg</div>
            </div>
          </div>

          <div className="space-y-3">
            {plan.batches.map((batch) => (
              <div key={batch.batch_number} className="border border-gray-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">
                    Batch #{batch.batch_number}
                  </span>
                  <span className="text-sm font-medium text-orange-700 bg-orange-50 px-2 py-0.5 rounded">
                    {parseFloat(batch.total_biomass_kg).toFixed(0)} kg
                  </span>
                </div>
                <div className="space-y-1">
                  {batch.patch_allocations.map((alloc, i) => (
                    <div key={i} className="flex items-center justify-between text-sm text-gray-600">
                      <span>{alloc.patch_code || alloc.patch_id?.slice(0, 8)}</span>
                      <span>{parseFloat(alloc.biomass_kg).toFixed(0)} kg</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {selectedOperator && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
              Operator: <strong>{selectedOperator.full_name}</strong> &middot;
              Date: <strong>{new Date(productionDate).toLocaleDateString()}</strong>
            </div>
          )}

          <div className="flex justify-between mt-6">
            <button
              type="button"
              onClick={() => { setPlan(null); setStep('select') }}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Back to Selection
            </button>
            <button
              type="button"
              onClick={handleConfirmCreate}
              disabled={createMutation.isPending}
              className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {createMutation.isPending ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Creating Batches...
                </>
              ) : (
                <>
                  <Flame className="h-5 w-5" />
                  Confirm & Create {plan.num_batches} Batch{plan.num_batches > 1 ? 'es' : ''}
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Cancel */}
      {step === 'select' && (
        <div className="flex justify-start">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  )
}
